import {useCallback, useEffect, useMemo, useRef, type ReactNode} from 'react'
import {announce} from '@github-ui/aria-live'
import {getEnv} from '@github-ui/client-env'
import {SidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {SidebarTooltip} from '@github-ui/layout-primitives/SidebarTooltip'
import {isFeatureEnabled, isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
import {useGroupedTasksData} from '@github-ui/agent-sessions/hooks/use-grouped-tasks-data'
import {usePinnedSidebarTasks} from '@github-ui/agent-sessions/hooks/use-task-pins'
import {useFilterableAgents, withBuiltinIdeAgentFallbacks} from '@github-ui/agent-sessions/hooks/use-filterable-agents'
import {useSidebarRepositoryFilter} from '@github-ui/agent-sessions/hooks/use-sidebar-repository-filter'
import {useSidebarGroupBy} from '@github-ui/agent-sessions/hooks/use-sidebar-group-by'
import {useTasksCore} from '@github-ui/agent-sessions/hooks/useTasks'
import {canPrepareTaskComposerChat} from '@github-ui/one-copilot-composer/shared'
import {isSandboxOptionAvailable} from '@github-ui/one-copilot-composer/use-environment-selection'
import {AgentsSidebar, type AgentsSidebarProps} from '@github-ui/agent-sessions/components/agents-view/AgentsSidebar'
import type {APITaskGroup} from '@github-ui/agent-sessions/types/api-task'
import {getReadableChatSessionState} from '@github-ui/copilot-chat-app/runtime/chat-history'
import {useSearchParams} from '@github-ui/use-navigate'
import {
  getEffectiveSidebarFilters,
  isNonRepositoryGrouping,
  resolveSidebarFacetParams,
  sidebarFacetsNeedAgentMetadata,
} from '@github-ui/agent-sessions/utils/filter-utils'
import {readSidebarFiltersFromSearchParams} from '@github-ui/agent-sessions/utils/session-filter-url'
import {useSafeChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {useQuery} from '@github-ui/react-query'
import {SidebarCollapseIcon, SidebarExpandIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'

import {CollapseSidebarOnContentChange} from './CollapseSidebarOnContentChange'
import {AgentsSidebarUserMenu} from './AgentsSidebarUserMenu'
import {CopilotChatProviders} from './CopilotChatProviders'
import {fetchCopilotChatPayload, type ChatPayload} from './copilot-chat-payload-route-query'
import {isChatSidebarEnabled, isQuickChatEntryPointEnabled, useAgentsChats} from './use-agents-chats'
import {useRepositoryAgentEnablement} from './use-repository-agent-enablement'
import styles from './CopilotAgentsPage.module.css'
import {shouldUseProvisionalQuickChatTitle} from '../utils/is-quick-chat-task'

const AGENTS_SIDEBAR_STORAGE_KEY = 'copilot-agents-sidebar'

// Sidebar props that vary per page; onPlusButtonClick is wired to closeDialog below.
type PerPageSidebarProps = Pick<
  AgentsSidebarProps,
  | 'activeTaskId'
  | 'onNavigateHome'
  | 'needsAttentionCount'
  | 'navigateToAgentsPage'
  | 'activeThreadId'
  | 'activeTaskIdForDeleteRedirect'
  | 'isSpacesActive'
  | 'isCustomizeActive'
>

interface AgentsSidebarLayoutProps {
  agentsEnabled: boolean
  currentUserLogin: string | undefined
  currentUserId: number | undefined
  /** Required so no route silently omits the Sandbox policy decision. */
  enterpriseSandboxEnabled: boolean
  taskGroups: APITaskGroup[] | undefined
  contentKey: string
  showLegacyChatHistory?: boolean
  legacyChatPayload?: ChatPayload
  legacyChatPayloadPending?: boolean
  legacyChatPayloadFailed?: boolean
  /** Disable when the page's route already loads the chat payload, including while that request is pending. */
  fetchLegacyChatPayload?: boolean
  chatApiURL?: string
  /** Identity of the displayed conversation; selects its Chats row and must never be gated on renderer choice. */
  activeTaskId?: string
  taskTitleOverride?: {taskId: string; title: string}
  showSpacesNav?: boolean
  showCustomizeNav?: boolean
  sidebarProps?: PerPageSidebarProps
  children: ReactNode
}

function SidebarFooterToggle({isExpanded, toggle}: {isExpanded: boolean; toggle: () => void}) {
  const {isInDialog, sidebarId, visualIsExpanded} = useCollapsibleSidebarContext()
  if (isInDialog) return null

  const label = isExpanded ? 'Collapse' : 'Expand sidebar'
  return (
    <SidebarTooltip text={label}>
      {/* eslint-disable-next-line primer-react/a11y-remove-disable-tooltip -- SidebarTooltip provides the visible hover and focus label */}
      <IconButton
        icon={visualIsExpanded ? SidebarExpandIcon : SidebarCollapseIcon}
        aria-label={label}
        unsafeDisableTooltip
        aria-expanded={isExpanded}
        aria-controls={sidebarId}
        variant="invisible"
        onClick={toggle}
      />
    </SidebarTooltip>
  )
}

/**
 * Shared scaffold for Agents and Spaces pages. Owns absolute sidebar navigation,
 * task/group fetching, and flag-gated legacy history so navigation stays consistent
 * across pages. Callers can reuse an existing chat payload or parent provider.
 */
export function AgentsSidebarLayout({
  agentsEnabled,
  currentUserLogin,
  currentUserId,
  enterpriseSandboxEnabled,
  taskGroups,
  contentKey,
  showLegacyChatHistory = isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out'),
  legacyChatPayload,
  legacyChatPayloadPending = false,
  legacyChatPayloadFailed = false,
  fetchLegacyChatPayload = true,
  chatApiURL,
  activeTaskId,
  taskTitleOverride,
  showSpacesNav = false,
  showCustomizeNav = false,
  sidebarProps,
  children,
}: AgentsSidebarLayoutProps) {
  const [searchParams] = useSearchParams()
  const sidebarFilteringEnabled = isFeatureEnabled('copilot_mission_control_sidebar_filtering')
  const sidebarFacetFilteringEnabled =
    sidebarFilteringEnabled && isFeatureEnabled('copilot_mission_control_sidebar_facet_filters')
  const sidebarTypeFacetEnabled =
    sidebarFacetFilteringEnabled && isFeatureEnabled('copilot_mission_control_sidebar_type_filter')
  const facetOptions = {includeFacets: sidebarFacetFilteringEnabled, includeTypeFacet: sidebarTypeFacetEnabled}
  const sidebarFilters = readSidebarFiltersFromSearchParams(searchParams, currentUserLogin, facetOptions)
  const effectiveSidebarFilters = getEffectiveSidebarFilters(sidebarFilters, facetOptions)
  // Status filtering stays flag-gated; the repo: filter below works even with the flag off.
  const sidebarStatuses =
    sidebarFilteringEnabled && effectiveSidebarFilters.statuses.length
      ? [...new Set(effectiveSidebarFilters.statuses)].sort()
      : undefined
  const sidebarRepo = effectiveSidebarFilters.repo
  const hasSidebarFacetFilter = Boolean(
    sidebarFacetFilteringEnabled &&
    effectiveSidebarFilters &&
    (effectiveSidebarFilters.types.length > 0 || effectiveSidebarFilters.agents.length > 0),
  )
  const needsFilterableAgents = Boolean(
    sidebarFacetFilteringEnabled && sidebarFilters && sidebarFacetsNeedAgentMetadata(sidebarFilters, facetOptions),
  )
  const filterableAgentsQuery = useFilterableAgents({enabled: needsFilterableAgents, preserveErrors: true})
  const filterableAgents = useMemo(
    () => (filterableAgentsQuery.data ? withBuiltinIdeAgentFallbacks(filterableAgentsQuery.data) : undefined),
    [filterableAgentsQuery.data],
  )
  const sidebarFacetResolution =
    sidebarFacetFilteringEnabled && sidebarFilters
      ? resolveSidebarFacetParams(
          sidebarFilters,
          filterableAgents,
          {
            agentsLoaded: !needsFilterableAgents || filterableAgentsQuery.isSuccess,
            agentsError: filterableAgentsQuery.isError,
          },
          facetOptions,
        )
      : {kind: 'none' as const}
  const sidebarRepository = useSidebarRepositoryFilter(sidebarRepo, taskGroups)
  const hasParentChatProvider = Boolean(useSafeChatState())
  const quickChatEnabled = isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- This user-specific legacy provider payload should not persist and its compatibility endpoint has no schema-aware fetch helper.
  const chatPayloadQuery = useQuery({
    queryKey: ['copilot-agents', 'chat-payload'],
    queryFn: fetchCopilotChatPayload,
    enabled:
      (showLegacyChatHistory || quickChatEnabled) &&
      fetchLegacyChatPayload &&
      !legacyChatPayload &&
      !hasParentChatProvider,
    retry: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })
  const {
    data: fetchedChatPayload,
    isError: fetchedChatPayloadFailed,
    isLoading: fetchedChatPayloadPending,
  } = chatPayloadQuery
  const chatPayload = legacyChatPayload ?? fetchedChatPayload
  const chatPayloadFailed = legacyChatPayloadFailed || fetchedChatPayloadFailed
  const defaultChatApiURL = getEnv().copilotApiOverrideUrl
  const resolvedChatApiURL = chatApiURL ?? defaultChatApiURL
  const sidebarChatApiURL =
    !isFeatureEnabled('copilot_mission_control_use_api_gateway') && resolvedChatApiURL !== defaultChatApiURL
      ? resolvedChatApiURL
      : undefined
  const taskDetailEnabled = isFeatureEnabled('copilot_mission_control_agents_task_detail')
  const chatSidebarEnabled = isChatSidebarEnabled({
    agentsEnabled,
    quickChatEnabled,
    taskDetailEnabled,
    apiURL: resolvedChatApiURL,
  })
  const canCreateChat =
    agentsEnabled &&
    isQuickChatEntryPointEnabled({quickChatEnabled, taskDetailEnabled}) &&
    canPrepareTaskComposerChat({
      chatLaunch: {kind: 'environment'},
      hasEnvironmentSubmit: true,
      hideRepoSelector: false,
      showSandboxOption: isSandboxOptionAvailable({
        isEnvironmentsEnabled: isFeatureEnabled('copilot_mission_control_environments_web_kickoff'),
        isManagedSandboxEnabled: isFeatureEnabled('copilot_mission_control_managed_sandbox_environments'),
        supportsManagedSandbox: enterpriseSandboxEnabled,
        hasEnvironmentSubmit: true,
      }),
      // New chat opens without inheriting the repository from the current composer.
      hasSpecifiedRepository: false,
      scopeLogin: currentUserLogin,
    })
  const hasLegacyChatPayloadRequest = Boolean(
    chatPayload || hasParentChatProvider || fetchLegacyChatPayload || legacyChatPayloadPending || chatPayloadFailed,
  )
  const suppressV2ChatResults = showLegacyChatHistory && hasLegacyChatPayloadRequest
  const legacyChatHistoryFailed = Boolean(
    showLegacyChatHistory && chatPayloadFailed && !chatPayload && !hasParentChatProvider,
  )
  const hasPendingLegacyChatPayload = legacyChatPayloadPending || fetchedChatPayloadPending
  const legacyChatHistoryPending = Boolean(
    showLegacyChatHistory &&
    !chatPayload &&
    !hasParentChatProvider &&
    !chatPayloadFailed &&
    hasPendingLegacyChatPayload,
  )
  const canShowLegacyChatHistory = Boolean(showLegacyChatHistory && (chatPayload || hasParentChatProvider))
  const agentsChats = useAgentsChats({
    apiURL: resolvedChatApiURL,
    enabled: chatSidebarEnabled,
    announceResults: !suppressV2ChatResults,
  })
  const sidebarStatesRef = useRef(
    new Map<string, {isLoading: boolean; isError: boolean; isFilteredEmpty: boolean; isRepositoryNotFound: boolean}>(),
  )
  const wasFilteredEmptyRef = useRef(false)
  const wasRepositoryNotFoundRef = useRef(false)
  const chatHistoryStateRef = useRef({
    chatSidebarEnabled,
    canShowLegacyChatHistory,
    legacyChatHistoryFailed,
    legacyChatPayloadFailed,
    suppressV2ChatResults,
    isErrorChats: agentsChats.isErrorChats,
    isLoadingChats: agentsChats.isLoadingChats,
  })
  const announcedChatFailuresRef = useRef(new Set<'full' | 'partial'>())
  const evaluateChatFailures = useCallback(() => {
    const {
      chatSidebarEnabled: v2Enabled,
      canShowLegacyChatHistory: legacyEnabled,
      legacyChatHistoryFailed: legacyFailed,
      legacyChatPayloadFailed: pageOwnedLegacyFailed,
      suppressV2ChatResults: suppressV2Results,
      isErrorChats,
      isLoadingChats,
    } = chatHistoryStateRef.current
    const legacyStates = Array.from(sidebarStatesRef.current.values())
    const legacyStateError = legacyEnabled && legacyStates.some(state => state.isError)
    const legacyError = legacyFailed || legacyStateError
    const v2Error = v2Enabled && isErrorChats
    if (!v2Error && !legacyError) {
      announcedChatFailuresRef.current.clear()
      return
    }
    if (!legacyEnabled && !legacyFailed) {
      if (v2Error) {
        if (suppressV2Results) return
        announcedChatFailuresRef.current.add('full')
      }
      return
    }
    if (
      (!legacyFailed && (legacyStates.length === 0 || legacyStates.some(state => state.isLoading))) ||
      (v2Enabled && isLoadingChats)
    )
      return

    const failure = (!v2Enabled || v2Error) && legacyError ? 'full' : 'partial'
    if (announcedChatFailuresRef.current.has(failure)) return
    announcedChatFailuresRef.current.add(failure)
    if (pageOwnedLegacyFailed && !v2Error && !legacyStateError) return
    if (failure === 'full') announce('Chats could not be loaded.', {assertive: true})
    else announce('Couldn’t load some chats')
  }, [])
  const onSidebarStateChange = useCallback(
    (
      sidebarId: string,
      state?: {isLoading: boolean; isError: boolean; isFilteredEmpty: boolean; isRepositoryNotFound: boolean},
    ) => {
      const states = sidebarStatesRef.current
      let wasLoading = false
      for (const value of states.values()) {
        if (value.isLoading) {
          wasLoading = true
          break
        }
      }
      if (state) states.set(sidebarId, state)
      else states.delete(sidebarId)
      const currentStates = Array.from(states.values())
      const isFilteredEmpty = currentStates.some(value => value.isFilteredEmpty)
      if (isFilteredEmpty && !wasFilteredEmptyRef.current)
        announce('No sessions match your filters. Try adjusting your search or filters.')
      wasFilteredEmptyRef.current = isFilteredEmpty
      const isRepositoryNotFound = currentStates.some(value => value.isRepositoryNotFound)
      if (isRepositoryNotFound && !wasRepositoryNotFoundRef.current) announce('Repository not found.')
      wasRepositoryNotFoundRef.current = isRepositoryNotFound
      if (
        !(chatHistoryStateRef.current.chatSidebarEnabled && chatHistoryStateRef.current.isLoadingChats) &&
        !wasLoading &&
        currentStates.some(value => value.isLoading)
      ) {
        announce('Loading chats.')
      }
      evaluateChatFailures()
    },
    [evaluateChatFailures],
  )
  useEffect(() => {
    chatHistoryStateRef.current = {
      chatSidebarEnabled,
      canShowLegacyChatHistory,
      legacyChatHistoryFailed,
      legacyChatPayloadFailed,
      suppressV2ChatResults,
      isErrorChats: agentsChats.isErrorChats,
      isLoadingChats: agentsChats.isLoadingChats,
    }
    evaluateChatFailures()
  }, [
    agentsChats.isErrorChats,
    agentsChats.isLoadingChats,
    canShowLegacyChatHistory,
    chatSidebarEnabled,
    evaluateChatFailures,
    legacyChatHistoryFailed,
    legacyChatPayloadFailed,
    suppressV2ChatResults,
  ])

  // Server-grouped sidebar data (repository pre-hydrated), seeded from SSR to avoid a
  // client round-trip.
  const {
    groups: sidebarGroups,
    isLoading: isLoadingSidebarGroups,
    isError: isErrorSidebarGroups,
  } = useGroupedTasksData({
    taskFilter: 'active',
    // Show all repos with recent activity, not just the server default of 10.
    maxGroups: 50,
    enabled:
      agentsEnabled &&
      (!sidebarRepo || Boolean(sidebarRepository.repository)) &&
      sidebarFacetResolution.kind !== 'resolving' &&
      sidebarFacetResolution.kind !== 'unresolvable',
    scope:
      sidebarFacetResolution.kind === 'resolving' || sidebarFacetResolution.kind === 'unresolvable'
        ? 'sidebar-unresolved-facets'
        : sidebarRepo && !sidebarRepository.repository
          ? 'sidebar-unresolved-repo'
          : 'sidebar',
    statuses: sidebarStatuses,
    repoId: sidebarRepository.repository?.id,
    initialGroups: sidebarStatuses || sidebarRepo || hasSidebarFacetFilter ? undefined : taskGroups,
    ...(sidebarFacetResolution.kind === 'ready' ? sidebarFacetResolution.params : {}),
  })
  const pins = usePinnedSidebarTasks(agentsEnabled)
  const overriddenTaskId = taskTitleOverride?.taskId
  const overriddenTitle = taskTitleOverride?.title
  const displayedPins = {
    ...pins,
    tasks: pins.tasks.map(task =>
      task.id === overriddenTaskId && overriddenTitle && shouldUseProvisionalQuickChatTitle(task.name)
        ? {...task, name: overriddenTitle}
        : task,
    ),
  }
  const hideSidebarGroups =
    sidebarFacetResolution.kind === 'resolving' || sidebarFacetResolution.kind === 'unresolvable'
  const displayedSidebarGroups = useMemo(() => {
    const sourceGroups = hideSidebarGroups ? [] : sidebarGroups
    return overriddenTaskId && overriddenTitle
      ? sourceGroups.map(
          ([group, tasks]) =>
            [
              group,
              tasks.map(task =>
                task.id === overriddenTaskId && shouldUseProvisionalQuickChatTitle(task.name)
                  ? {...task, name: overriddenTitle}
                  : task,
              ),
            ] as const,
        )
      : sourceGroups
  }, [hideSidebarGroups, overriddenTaskId, overriddenTitle, sidebarGroups])
  const displayedAgentsChats = useMemo(
    () =>
      overriddenTaskId && overriddenTitle
        ? {
            ...agentsChats,
            chats: agentsChats.chats.map(chat =>
              chat.taskId === overriddenTaskId && shouldUseProvisionalQuickChatTitle(chat.title)
                ? {...chat, title: overriddenTitle}
                : chat,
            ),
          }
        : agentsChats,
    [agentsChats, overriddenTaskId, overriddenTitle],
  )
  const {groupBy} = useSidebarGroupBy({
    urlGroupBy: sidebarFilters.groupBy,
    currentUserLogin,
    filteringEnabled: sidebarFilteringEnabled,
  })
  const repositoryActionsVisible = !isNonRepositoryGrouping(groupBy)
  const repositoryAgentEnabledById = useRepositoryAgentEnablement(
    displayedSidebarGroups,
    agentsEnabled && repositoryActionsVisible,
  )
  const resolvedSidebarProps = {
    ...sidebarProps,
    chatApiURL: chatSidebarEnabled ? sidebarChatApiURL : undefined,
  }

  // useTasksCore's alive-channel subscription doesn't gate on `enabled` the way
  // useGroupedTasksData's does, so disable its polling explicitly when the count
  // itself is disabled to avoid an unwanted alive-channels request.
  const needsAttentionFetchEnabled = agentsEnabled && currentUserId != null
  const {counts: needsAttentionCounts} = useTasksCore({
    taskFilter: 'active',
    includeCounts: true,
    pageSize: 1,
    creatorIds: currentUserId != null ? [currentUserId] : undefined,
    enabled: needsAttentionFetchEnabled,
    scope: 'needs-attention-count',
    sessionsPollingInterval: needsAttentionFetchEnabled ? undefined : false,
  })
  const needsAttentionCount = needsAttentionCounts?.needsAttention

  return (
    <SidebarPageLayout
      storageKey={AGENTS_SIDEBAR_STORAGE_KEY}
      fitViewport="document"
      fillHeight
      className={styles.viewportLayout}
    >
      <SidebarPageLayout.Sidebar
        aria-label="Agent navigation"
        expandedWidth="large"
        scrollable
        scrollRestorationKey={AGENTS_SIDEBAR_STORAGE_KEY}
        footer={
          quickChatEnabled && currentUserLogin
            ? ({isExpanded, toggle}) => (
                <div className={styles.sidebarFooter} data-expanded={isExpanded} data-testid="agents-sidebar-footer">
                  <AgentsSidebarUserMenu
                    currentUserLogin={currentUserLogin}
                    isExpanded={isExpanded}
                    chatPayload={hasParentChatProvider ? undefined : chatPayload}
                    isError={chatPayloadFailed && !chatPayload && !hasParentChatProvider}
                  />
                  <SidebarFooterToggle isExpanded={isExpanded} toggle={toggle} />
                </div>
              )
            : undefined
        }
      >
        <CollapseSidebarOnContentChange contentKey={contentKey}>
          {({closeDialog}) => {
            const sidebar = (
              <AgentsSidebar
                pins={displayedPins}
                currentUserLogin={currentUserLogin}
                groups={displayedSidebarGroups}
                repositoryAgentEnabledById={repositoryAgentEnabledById}
                isLoadingTasks={
                  isLoadingSidebarGroups || sidebarRepository.isLoading || sidebarFacetResolution.kind === 'resolving'
                }
                isErrorTasks={isErrorSidebarGroups}
                facetResolutionError={
                  sidebarFacetResolution.kind === 'unresolvable' && sidebarFacetResolution.reason === 'metadata-error'
                }
                sidebarRepository={sidebarRepository.repository}
                repositoryNotFound={sidebarRepository.isError}
                filterableAgents={sidebarFacetFilteringEnabled ? filterableAgents : undefined}
                showLegacyChatHistory={canShowLegacyChatHistory}
                legacyChatHistoryPending={legacyChatHistoryPending}
                legacyChatHistoryFailed={legacyChatHistoryFailed}
                onSidebarStateChange={onSidebarStateChange}
                onPlusButtonClick={closeDialog}
                agentsChats={chatSidebarEnabled ? displayedAgentsChats : undefined}
                canCreateChat={canCreateChat}
                activeChatTaskId={activeTaskId}
                getChatStatusLabel={chat => getReadableChatSessionState(chat.latestSessionState)}
                onChatNavigate={closeDialog}
                showSpacesNav={showSpacesNav}
                showCustomizeNav={showCustomizeNav}
                navigateToAgentsPage
                needsAttentionCount={needsAttentionCount}
                {...resolvedSidebarProps}
              />
            )

            return canShowLegacyChatHistory && chatPayload && !hasParentChatProvider ? (
              <CopilotChatProviders chatPayload={chatPayload}>{sidebar}</CopilotChatProviders>
            ) : (
              sidebar
            )
          }}
        </CollapseSidebarOnContentChange>
      </SidebarPageLayout.Sidebar>
      {children}
    </SidebarPageLayout>
  )
}
